/**
 * The "Taut String" route map (approved design R2B).
 *
 * A long grey line loops back on itself through the things that waste a
 * learner's time; the green route is the same journey pulled tight, touching
 * only the lesson sets that matter, and ending at a trophy.
 *
 * The three green stops are real buttons: hover or focus opens the lesson set.
 */

interface Milestone {
  /** Percentage position over the map frame. */
  left: string;
  top: string;
  index: string;
  title: string;
  lessonCount: string;
  totalMinutes: string;
  lessons: Array<{ name: string; minutes: string }>;
  pingDelay?: string;
}

const MILESTONES: Milestone[] = [
  {
    left: "30.5%",
    top: "57.2%",
    index: "01",
    title: "Foundations",
    lessonCount: "3 lessons",
    totalMinutes: "3 LESSONS · 14 MIN",
    lessons: [
      { name: "What a pivot table is", minutes: "4 min" },
      { name: "Rows, columns, values", minutes: "5 min" },
      { name: "Your first table", minutes: "5 min" },
    ],
  },
  {
    left: "53%",
    top: "52.6%",
    index: "02",
    title: "Working skills",
    lessonCount: "4 lessons",
    totalMinutes: "4 LESSONS · 22 MIN",
    pingDelay: "2s",
    lessons: [
      { name: "Grouping & filtering", minutes: "6 min" },
      { name: "Calculated fields", minutes: "6 min" },
      { name: "Check · prove it", minutes: "4 min" },
      { name: "Common mistakes", minutes: "6 min" },
    ],
  },
  {
    left: "72.9%",
    top: "45.8%",
    index: "03",
    title: "Real data",
    lessonCount: "3 lessons",
    totalMinutes: "3 LESSONS · 18 MIN",
    pingDelay: "4s",
    lessons: [
      { name: "A messy real dataset", minutes: "7 min" },
      { name: "Build the report", minutes: "7 min" },
      { name: "Final check", minutes: "4 min" },
    ],
  },
];

// Positioned to clear the green milestone tags, which sit just below their dots.
const PAIN_POINTS = [
  { x: 252, y: 92, label: "40-hour course" },
  { x: 300, y: 174, label: "long videos" },
  { x: 520, y: 324, label: "boring lectures" },
  { x: 706, y: 232, label: "video fatigue" },
  { x: 840, y: 324, label: "stuff you already know" },
  { x: 1045, y: 268, label: "no feedback" },
];

const GREY_STATIONS = [
  [220, 235], [300, 150], [252, 112], [320, 210], [520, 300], [660, 330],
  [672, 272], [840, 300], [960, 200], [1040, 130], [1053, 177],
];

export function RouteMap() {
  return (
    <div className="route-frame">
      <svg
        viewBox="0 0 1180 430"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="A long grey line loops back on itself through forty-hour courses, long videos and lessons you don't need, while a short green route runs straight through three lesson sets to a trophy."
      >
        <path
          className="gray-line"
          d="M 70 280 C 140 275 170 250 220 235 C 260 224 300 190 300 150 C 300 105 245 100 230 135 C 218 165 260 195 320 210 C 400 230 460 260 520 300 C 560 328 620 350 660 330 C 700 310 690 265 650 265 C 610 265 600 305 650 330 C 700 355 780 330 840 300 C 890 275 930 240 960 200 C 980 172 1010 140 1040 130 C 1070 120 1085 140 1075 165 C 1066 187 1030 185 1020 160 C 1012 140 1030 115 1060 105 C 1080 98 1096 102 1105 112"
        />
        <g className="gray-st">
          {GREY_STATIONS.map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={5} />
          ))}
        </g>
        {PAIN_POINTS.map((point) => (
          <text key={point.label} className="pain" x={point.x} y={point.y} textAnchor="middle">
            {point.label}
          </text>
        ))}

        <path
          id="mkRoute"
          className="route-line route-dash"
          d="M 70 280 C 200 268 320 250 440 240 C 570 229 690 216 810 200 C 900 188 1000 170 1080 155"
        />

        <circle cx={70} cy={280} r={12} fill="var(--acc)" />
        <circle cx={70} cy={280} r={12} fill="none" stroke="var(--acc)" strokeWidth={2}>
          <animate attributeName="r" values="12;26;12" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values=".7;0;.7" dur="3s" repeatCount="indefinite" />
        </circle>
        <text className="svg-lab lab-you" x={70} y={322} textAnchor="middle">YOU ARE HERE</text>

        <g className="traveler-g">
          <circle className="traveler" r={7} fill="var(--acc)">
            <animateMotion
              dur="11s"
              repeatCount="indefinite"
              keyPoints="0;0;1;1"
              keyTimes="0;0.04;0.5;1"
              calcMode="linear"
            >
              <mpath href="#mkRoute" />
            </animateMotion>
          </circle>
        </g>

        <g transform="translate(1080,155)">
          <g className="tro-shine">
            <path className="tro-cup" d="M -15 -58 h30 v11 c0 13 -6.5 21 -15 23 c-8.5 -2 -15 -10 -15 -23 z" />
            <path d="M -15 -54 c-11 1 -15 10 -5 16" stroke="var(--amber)" strokeWidth={3.5} fill="none" />
            <path d="M 15 -54 c11 1 15 10 5 16" stroke="var(--amber)" strokeWidth={3.5} fill="none" />
            <rect className="tro-dark" x={-4} y={-25} width={8} height={9} rx={1} />
            <rect className="tro-cup" x={-13} y={-16} width={26} height={7} rx={2} />
          </g>
          <circle className="tro-spark" cx={-26} cy={-64} r={2.5} />
          <circle className="tro-spark sp2" cx={27} cy={-50} r={2} />
          <circle className="tro-spark sp3" cx={0} cy={-74} r={2} />
          <text className="svg-lab lab-tgt" x={0} y={-84} textAnchor="middle">YOUR TARGET</text>
        </g>
      </svg>

      {MILESTONES.map((milestone) => (
        <div className="np" key={milestone.index} style={{ left: milestone.left, top: milestone.top }}>
          <button
            className="ndot"
            type="button"
            aria-label={`Stop ${milestone.index}, ${milestone.title}, ${milestone.lessonCount}`}
            data-testid={`route-stop-${milestone.index}`}
          />
          <span className="ping" style={milestone.pingDelay ? { animationDelay: milestone.pingDelay } : undefined} />
          <span className="ntag">
            <b>{milestone.index} · {milestone.title}</b> · {milestone.lessonCount}
          </span>
          <div className="ncard">
            <h5>{milestone.title}</h5>
            <div className="meta">{milestone.totalMinutes}</div>
            <ul>
              {milestone.lessons.map((lesson) => (
                <li key={lesson.name}>
                  <span>{lesson.name}</span>
                  <span>{lesson.minutes}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}
